import { curatorStudentsApiSlice } from './curatorStudents'
import { ratingApiSlice } from '../rating/ratingSlice'

export type PracticeMark = 'WITH_CAMERA' | 'WITHOUT_CAMERA' | 'ABSENT'

// bff: 0 / "" -> null
export interface MrMentor {
    id: number
    name: string | null
}

export interface MrGroup {
    groupId: number
    groupTitle: string | null
    courseId: number | null
    courseSubjectId: number
    subjectId: number | null
    subjectTitleKz: string | null
    subjectTitleRu: string | null
    mentors: MrMentor[]
    canEdit: boolean
}

export interface MrStudent {
    userSubjectEnrollId: number
    userId: number | null
    firstName: string | null
    lastName: string | null
    practiceStatus: PracticeMark | null
    practice2Status: PracticeMark | null
}

export interface MrSession {
    id: number | null
    sessionNumber: number
    title: string | null
    recordLink: string | null
    fileUrl: string | null
    fileName: string | null
    topic: string | null
    summary: string | null
    problems: string | null
    mentorId: number | null
    mentorName: string | null
    updatedAt: string | null
    filled: boolean
}

export interface MrPsGroup {
    group: MrGroup
    students: MrStudent[]
    sessions: MrSession[]
}

export interface MrRatingReport {
    id: number | null
    laggingIds: number[]
    actions: string | null
    plan: string | null
    mentorId: number | null
    mentorName: string | null
    updatedAt: string | null
    filled: boolean
}

export interface MrRatingGroup {
    group: MrGroup
    students: MrStudent[]
    report: MrRatingReport
}

export interface MrCell {
    weekNumber: number
    psFilled: number
    psTotal: number
    ratingFilled: boolean
    past: boolean
    missing: boolean
}

export interface MrComplianceRow {
    mentorId: number | null
    mentorName: string | null
    groupId: number
    groupTitle: string | null
    courseSubjectId: number
    subjectId: number | null
    subjectTitleKz: string | null
    subjectTitleRu: string | null
    weeks: MrCell[]
    missingCount: number
}

export interface MrWeekArgs {
    courseId?: number | null
    groupId?: number | null
    monthNumber: number
    weekNumber: number
    mentorId?: number
}

export interface MrSavePsArgs {
    groupId: number
    courseSubjectId: number
    monthNumber: number
    weekNumber: number
    sessionNumber: number
    title: string
    recordLink: string
    topic: string
    summary: string
    problems: string
    removeFile: boolean
    attendance: { userSubjectEnrollId: number; status: PracticeMark | '' }[]
    file?: File | null
}

export interface MrSaveRatingArgs {
    groupId: number
    courseSubjectId: number
    monthNumber: number
    weekNumber: number
    laggingIds: number[]
    actions: string
    plan: string
}

const weekParams = (a: MrWeekArgs) => ({
    courseId: a.courseId || 0,
    groupId: a.groupId || 0,
    monthNumber: a.monthNumber,
    weekNumber: a.weekNumber,
    mentorId: a.mentorId || 0,
})

export const mentorReportApi = curatorStudentsApiSlice
    .enhanceEndpoints({ addTagTypes: ['MrPs', 'MrRating', 'MrCompliance'] })
    .injectEndpoints({
        endpoints: (build) => ({
            fetchPsWeek: build.query<MrPsGroup[], MrWeekArgs>({
                query: (a) => ({ url: `mentor-report/ps`, params: weekParams(a) }),
                transformResponse: (response: { data: MrPsGroup[] }) => response.data ?? [],
                providesTags: ['MrPs'],
            }),
            savePs: build.mutation<MrSession, MrSavePsArgs>({
                query: ({ file, attendance, ...fields }) => {
                    const body = new FormData()
                    Object.entries(fields).forEach(([k, v]) => body.append(k, String(v ?? '')))
                    body.append('attendance', JSON.stringify(attendance))
                    if (file) body.append('file', file)
                    return { url: `mentor-report/ps`, method: 'POST', body }
                },
                invalidatesTags: ['MrPs', 'MrRating', 'MrCompliance'],
                // посещение пишется в те же отметки, что в «Практика және зачет»
                async onQueryStarted(_a, { dispatch, queryFulfilled }) {
                    try {
                        await queryFulfilled
                        dispatch(ratingApiSlice.util.invalidateTags([{ type: 'Rating', id: 'LIST' }, 'Report']))
                    } catch (e) {}
                },
            }),
            fetchRatingWeek: build.query<MrRatingGroup[], MrWeekArgs>({
                query: (a) => ({ url: `mentor-report/rating`, params: weekParams(a) }),
                transformResponse: (response: { data: MrRatingGroup[] }) => response.data ?? [],
                providesTags: ['MrRating'],
            }),
            saveRating: build.mutation<MrRatingReport, MrSaveRatingArgs>({
                query: (body) => ({ url: `mentor-report/rating`, method: 'PUT', body }),
                invalidatesTags: ['MrRating', 'MrCompliance'],
            }),
            fetchCompliance: build.query<
                { monthNumber: number; rows: MrComplianceRow[] },
                { courseId?: number | null; groupId?: number | null; mentorId?: number; monthNumber: number }
            >({
                query: (a) => ({
                    url: `mentor-report/compliance`,
                    params: { courseId: a.courseId || 0, groupId: a.groupId || 0, mentorId: a.mentorId || 0, monthNumber: a.monthNumber },
                }),
                transformResponse: (response: { data: { monthNumber: number; rows: MrComplianceRow[] } }) => response.data,
                providesTags: ['MrCompliance'],
            }),
        }),
    })

export const studentName = (s: MrStudent) => `${s.lastName ?? ''} ${s.firstName ?? ''}`.trim() || `#${s.userSubjectEnrollId}`
